import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ShoppingCart, 
  ArrowLeft, 
  Minus, 
  Plus, 
  Truck, 
  Shield, 
  Check, 
  Star,
  Info 
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useCart } from '../hooks/useCart';
import { productService } from '../services/productService';
import type { FirebaseProduct } from '../types/product';

const ProductImages: React.FC<{
  images: { url: string; alt: string }[];
  selectedImage: number;
  onImageSelect: (index: number) => void;
}> = ({ images, selectedImage, onImageSelect }) => (
  <div className="space-y-4">
    <div className="aspect-square bg-white rounded-lg shadow-sm overflow-hidden">
      <img
        src={images[selectedImage]?.url || '/placeholder.png'}
        alt={images[selectedImage]?.alt || 'Product image'}
        className="w-full h-full object-contain p-4"
      />
    </div>

    {images.length > 1 && (
      <div className="grid grid-cols-4 gap-4">
        {images.map((image, index) => (
          <button
            key={image.url}
            onClick={() => onImageSelect(index)}
            className={`aspect-square bg-white rounded-lg overflow-hidden border-2 transition-colors ${
              selectedImage === index ? 'border-brand-blue-500' : 'border-transparent hover:border-gray-200'
            }`}
          >
            <img src={image.url} alt={image.alt} className="w-full h-full object-contain p-2" />
          </button>
        ))}
      </div>
    )}
  </div>
);

const ProductHeader: React.FC<{
  category: string;
  sku: string;
  name: string;
  rating?: number;
  reviewCount?: number;
}> = ({ category, sku, name, rating, reviewCount }) => (
  <>
    <div className="flex items-center space-x-2 mb-2">
      <span className="text-sm text-brand-blue-500">{category}</span>
      <span className="text-sm text-gray-500">•</span>
      <span className="text-sm text-gray-500">SKU: {sku}</span>
    </div>
    <h1 className="text-3xl font-bold text-gray-900 mb-2">{name}</h1>
    {rating && reviewCount && (
      <div className="flex items-center mb-4">
        <div className="flex items-center text-yellow-400">
          <Star className="h-5 w-5 fill-current" />
          <span className="ml-1 text-lg font-medium text-gray-900">{rating}</span>
        </div>
        <span className="mx-2 text-gray-300">•</span>
        <span className="text-gray-600">{reviewCount} reviews</span>
      </div>
    )}
  </>
);

const PriceDisplay: React.FC<{ price: number | null | undefined }> = ({ price }) => {
  const isPriceOnRequest = !price || price === 0;

  if (isPriceOnRequest) {
    return (
      <div className="flex items-center text-brand-blue-500 mb-8">
        <Info className="h-6 w-6 mr-2" />
        <span className="text-2xl font-medium">Price on Request</span>
      </div>
    );
  }

  return (
    <div className="text-3xl font-bold text-gray-900 mb-8">
      {new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price)}
    </div>
  );
};

const ProductSpecifications: React.FC<{
  specifications: Record<string, string | undefined>;
}> = ({ specifications }) => {
  // Filter out empty specifications
  const validSpecs = Object.entries(specifications)
    .filter(([_, value]) => value && value.trim() !== '');

  if (validSpecs.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-8">
      {validSpecs.map(([key, value]) => (
        <div key={key} className="flex justify-between">
          <span className="text-gray-600 capitalize">{key}</span>
          <span className="font-medium">{value}</span>
        </div>
      ))}
    </div>
  );
};

const QuantitySelector: React.FC<{
  quantity: number;
  stock: number;
  onQuantityChange: (delta: number) => void;
}> = ({ quantity, stock, onQuantityChange }) => {
  if (stock === 0) {
    return (
      <div className="mb-8">
        <p className="text-red-500 font-medium">Out of Stock</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
      <div className="flex items-center space-x-4">
        <div className="flex items-center border rounded-lg">
          <button
            onClick={() => onQuantityChange(-1)}
            disabled={quantity <= 1}
            className="p-2 hover:bg-gray-100 transition-colors rounded-l-lg disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Decrease quantity"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="w-12 text-center border-x py-2">{quantity}</span>
          <button
            onClick={() => onQuantityChange(1)}
            disabled={quantity >= stock}
            className="p-2 hover:bg-gray-100 transition-colors rounded-r-lg disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Increase quantity"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <span className="text-sm text-gray-500">{stock} units available</span>
      </div>
    </div>
  );
};

const ProductFeatures: React.FC = () => (
  <div className="mt-8 grid grid-cols-2 gap-4">
    <div className="flex items-start space-x-3">
      <Truck className="h-5 w-5 text-brand-blue-500 flex-shrink-0" />
      <div>
        <h4 className="font-medium text-gray-900">Free Delivery</h4>
        <p className="text-sm text-gray-600">For orders above KES 5,000</p>
      </div>
    </div>
    <div className="flex items-start space-x-3">
      <Shield className="h-5 w-5 text-brand-blue-500 flex-shrink-0" />
      <div>
        <h4 className="font-medium text-gray-900">Quality Guarantee</h4>
        <p className="text-sm text-gray-600">100% original products</p>
      </div>
    </div>
  </div>
);

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<FirebaseProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  useEffect(() => {
    if (!slug) {
      console.error('Slug is missing in the URL');
      navigate('/products');
      return;
    }

    const fetchProduct = async () => {
      try {
        setLoading(true);
        const products = await productService.getProducts({ slug });
        const product = products[0];

        if (product) {
          setProduct(product);
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug, navigate]);

  const handleQuantityChange = (delta: number) => {
    if (!product) return;
    setQuantity((prev) => Math.max(1, Math.min(prev + delta, product.stock)));
  };

  const handleAddToCart = async () => {
    if (!product) return;

    try {
      const updatedProduct = await productService.getProduct(product.id);
      if (!updatedProduct || updatedProduct.stock < quantity) {
        setError('Not enough stock available');
        return;
      }

      addToCart(product, quantity);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (err) {
      setError('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
          <div className="space-y-4">
            <div className="h-4 bg-gray-100 rounded w-1/4 animate-pulse" />
            <div className="h-8 bg-gray-100 rounded w-3/4 animate-pulse" />
            <div className="h-4 bg-gray-100 rounded w-1/2 animate-pulse" />
            <div className="h-24 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Product not found'}</h2>
          <button
            onClick={() => navigate('/products')}
            className="text-brand-blue-500 hover:text-brand-blue-600"
          >
            Return to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{product.name} | Perfect Paper</title>
        <meta name="description" content={product.description} />
      </Helmet>

      {showSuccessToast && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in z-50">
          <Check className="h-5 w-5" />
          Added to cart successfully
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-brand-blue-500 hover:text-brand-blue-600 mb-8"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Products
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <ProductImages
            images={product.images}
            selectedImage={selectedImage}
            onImageSelect={setSelectedImage}
          />

          <div>
            <ProductHeader
              category={product.category}
              sku={product.sku}
              name={product.name}
              rating={product.rating}
              reviewCount={product.reviewCount}
            />

            <div className="prose prose-sm text-gray-600 mb-6">{product.description}</div>

            <PriceDisplay price={product.price} />

            <ProductSpecifications specifications={product.specifications} />

            <QuantitySelector
              quantity={quantity}
              stock={product.stock}
              onQuantityChange={handleQuantityChange}
            />

            <div className="flex items-center space-x-4 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className={`flex-1 py-3 px-6 rounded-lg font-medium flex items-center justify-center space-x-2 ${
                  product.stock === 0 
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                    : 'bg-brand-blue-500 text-white hover:bg-brand-blue-600'
                } transition-colors`}
              >
                <ShoppingCart className="h-5 w-5" />
                <span>{product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}</span>
              </button>
              
              <a
                href={`https://wa.me/254706788802?text=Hi, I'm interested in ${encodeURIComponent(product.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors flex items-center"
              >
                <FaWhatsapp className="h-5 w-5 mr-2" />
                Chat on WhatsApp
              </a>
            </div>

            <ProductFeatures />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetail;